/* eslint-disable no-console */
import { defineStore } from "pinia";
import Base from "+/Engine/Base";
import App from "+/Engine/App";

class FeedStore extends Base {

  // ----------- //
  // static vars //
  // ----------- //

  static #stores = {};
  static #queuedFeedRequests = {};
  static #queuedFeedRequestKeys = {};
  static #pollingStarted = false;
  static #syncInProgress = false;
  static #pollingTimerId;
  static #lastError;
  static #ttl = 5000;
  static #lastTransactionId = 0;
  static #transactionQueue = [];
  static #transactionsSent = [];

  // --------------------- //
  // Public Static Methods //
  // --------------------- //

  static getStore(appName, entityType, entityPk, fieldName) {
    const feedId = FeedStore.getId(appName, entityType, entityPk, fieldName);
    if (!FeedStore.#stores[feedId]) {
      throw new Error("cannot get feed store that does not exist");
    }
    return FeedStore.#stores[feedId];
  }

  static getId(appName, entityType, entityPk, fieldName) {
    return `app-${appName}-feed-${entityType}-${App.getPkString(entityPk)}-${fieldName}`;
  }

  // ------------- //
  // Instance Vars //
  // ------------- //

  #appName;
  #entityType;
  #entityPk;
  #fieldName;
  #fieldDef;
  #id;

  // ---------------- //
  // Instance Methods //
  // ---------------- //

  constructor(appName, entityType, entityPk, fieldName, fieldDef) {
    super();
    this.#appName = appName;
    this.#entityType = entityType;
    this.#entityPk = entityPk;
    this.#fieldName = fieldName;
    this.#fieldDef = fieldDef;
    this.#id = FeedStore.getId(appName, entityType, entityPk, fieldName, fieldDef);
    // user can currently create many objects with the same constructor param values which are then backed by exactly one pinia store via singleton pattern
    this.#createFeedStoreIfNotYetCreated();
  }

  get items() {
    return this.#myStore.myItems;
  }

  get orderedKeys() {
    return this.#myStore.orderedKeys;
  }

  get lastSeenItemIndex() {
    return this.#myStore.lastSeenItemIndex;
  }

  get lastSeenIndicatorItemPkString() {
    return this.#myStore.lastSeenIndicatorItemPkString;
  }

  set lastSeenItemIndex(value) {
    this.#myStore.lastSeenItemIndex = value;
  }

  get feedIsBeingViewed() {
    return this.#myStore.activelyViewing;
  }

  get latestPkString() {
    const latestItemKey = this.#myStore.orderedKeys.at(-1);

    if (!latestItemKey) {
      return null;
    }

    const splitItemKey = latestItemKey.split("-");
    splitItemKey.shift();
    return splitItemKey.join();
  }

  get lastKnownScrollPositionY() {
    return this.#myStore.lastKnownScrollPositionY;
  }

  set lastKnownScrollPositionY(scrollPositionY) {
    this.#myStore.lastKnownScrollPositionY = scrollPositionY;
  }

  // next //

  get hasMoreNext() {
    return this.#myStore.hasMoreNext;
  }

  updateLastSeenIndicator(indicatorPkString = null, isTempIndicatorPkString = false) {
    const store = this.#myStore;

    if (!store.lastSeenItemPkString) {
      return;
    }

    const targetPk = indicatorPkString ?? store.lastSeenItemPkString;
    const targetKey = `${this.#fieldDef.entity}-${targetPk}`;

    // remove old indicator
    if (store.lastSeenIndicatorItemPkString) {
      const oldItem = store.items[`${this.#fieldDef.entity}-${store.lastSeenIndicatorItemPkString}`];
      if (oldItem) {
        oldItem.isLastSeen = false;
      }
    }

    // only set indicator if item exists in current feed
    const newItem = store.items[targetKey];
    if (!newItem) {
      store.lastSeenIndicatorItemPkString = null;
      return;
    }

    newItem.isLastSeen = true;
    store.lastSeenIndicatorItemPkString = targetPk;

    if (indicatorPkString !== null) {
      this.#updateFeedLastSeen(indicatorPkString, isTempIndicatorPkString);
    }
  }

  updateLastSeenPkString(pkString) {
    const store = this.#myStore;
    store.lastSeenItemPkString = pkString;
    store.lastSeenItemIndex = store.orderedKeys.indexOf(`${this.#fieldDef.entity}-${pkString}`);
  }

  #setLastSeenFromServer(pkString) {
    const store = this.#myStore;
    store.lastSeenItemPkString = pkString;
    store.lastSeenItemIndex = store.orderedKeys.indexOf(`${this.#fieldDef.entity}-${pkString}`);
  }

  setViewStatus(status) {
    const store = this.#myStore;
    store.activelyViewing = status;
  }

  triggerInitialLoad() {
    if (!this.#myStore.hasMoreNext || this.#myStore.orderedKeys.length > 0) {
      return; // already loaded or nothing more to fetch
    }
    this.#paginateFeed("next");
  }

  loadMoreNext() {
    if (!this.#myStore.hasMoreNext) {
      throw new Error("do not request to loadMoreNext when hasMoreNext=false");
    }
    this.#paginateFeed("next");
  }

  get isLoadingNext() {
    return this.#myStore.isLoadingNext;
  }

  // True once at least one /feeds (or piggybacked /entities /
  // /transactions) response has been integrated into this store.
  // Until this flips, an empty `orderedKeys` could mean "fetch
  // hasn't completed yet" — callers that need to distinguish that
  // from "fetch completed and there are genuinely no items" must
  // gate on this getter rather than `orderedKeys.length`.
  get isInitiallyLoaded() {
    return this.#myStore.initiallyLoaded;
  }

  // previous //

  get hasMorePrevious() {
    return this.#myStore.hasMorePrevious;
  }

  loadMorePrevious() {
    if (!this.#myStore.hasMorePrevious) {
      throw new Error("do not request to loadMorePrevious when hasMorePrevious=false");
    }
    this.#paginateFeed("previous");
  }

  get isLoadingPrevious() {
    return this.#myStore.isLoadingPrevious;
  }

  // ---------------------- //
  // Private Static Methods //
  // ---------------------- //

  static #ensurePollingStarted() {
    if (this.#pollingStarted) {
      return;
    }
    this.#pollingStarted = true;
    this.#makeApiCalls();
  }

  #assertNextOffsetsAlign(feedResponse, direction) {
    // @todo i am not sure this is doing the correct thing
    const items = this.items;
    if (items.length === 0) {
      Object.values(feedResponse.offsets).forEach(offsetValue => {
        if (!["0", "", Number.MAX_SAFE_INTEGER.toString()].includes(offsetValue.toString())) {
          throw new Error("unexpected offset in feed response 1");
        }
      });
    } else {
      const originalLastItem = direction === "next" ? items.at(-1) : items[0];
      Object.keys(feedResponse.offsets).forEach(offsetName => {
        const offsetField = this.#fieldDef.offsets.find(offset => offset.name === offsetName).field;
        if (originalLastItem[offsetField] !== feedResponse[offsetName]) {
          throw new Error("unexpected offset in feed response 2");
        }
      });
    }
  }

  #computeNewOffsets(responseOffsets) {
    const newOffsets = {};
    this.#fieldDef.offsets.forEach(offset => {
      newOffsets[offset.name] = responseOffsets[offset.name];
    });
    return newOffsets;
  }

  compareEntities(a, b, offsets) {
    for (const offset of offsets) {
      let aValue = a[offset.field];
      let bValue = b[offset.field];

      // null safety
      if (aValue == null && bValue == null) {
        continue;
      }
      if (aValue == null) {
        return offset.direction === "asc" ? -1 : 1;
      }
      if (bValue == null) {
        return offset.direction === "asc" ? 1 : -1;
      }

      if (aValue < bValue) {
        return offset.direction === "asc" ? -1 : 1;
      }

      if (aValue > bValue) {
        return offset.direction === "asc" ? 1 : -1;
      }
    }

    return 0;
  }

  removeItemFromFeed(pkString) {
    const entityType = this.#fieldDef.entity;
    const keyedPkString = `${entityType}-${pkString}`;
    const index = this.#myStore.orderedKeys.indexOf(keyedPkString);
    if (index !== -1) {
      this.#myStore.orderedKeys.splice(index, 1);
      delete this.#myStore.items[keyedPkString];
    }
  }

  spliceItemIntoFeed(entity, pk) {
    // TODO: this function might trigger weird flickering on UI
    const app = App.get(this.#appName);
    const offsets = this.#fieldDef.offsets;
    const entityType = this.#fieldDef.entity;

    const pkString = `${entityType}-${App.getPkString(pk)}`;
    const tempPkString = `${entityType}-${entity.getTempPkString()}`;

    if (this.#myStore.orderedKeys.includes(pkString)) {
      return;
    }

    const tempItemIndex = this.#myStore.orderedKeys.indexOf(tempPkString);
    if (tempItemIndex !== -1) {
      this.#myStore.orderedKeys.splice(tempItemIndex, 1);
      delete this.#myStore.items[tempPkString];
    }

    let left = 0;
    let right = this.#myStore.orderedKeys.length;

    while (left < right) {
      // TODO: update this to math.floor
      const mid = left + right >> 1;

      const midKey = this.#myStore.orderedKeys[mid];
      const midPkString = midKey.slice(midKey.indexOf("-") + 1);
      const midPk = App.getPkFromString(midPkString);

      const midEntity = app.getEntity(entityType, midPk);

      const cmp = this.compareEntities(entity, midEntity, offsets);

      if (cmp > 0) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }

    this.#myStore.items[pkString] = {
      entityPk: entity.getPk(),
      entityType: entity.getType(),
      pkString: App.getPkString(pk),
    };

    this.#myStore.orderedKeys.splice(left, 0, pkString);
  }

  // Public entry point for applying a single feed result. Called by FeedStore's own
  // /feeds success handler and by App's shared mergeResponsePayload so /entities and
  // /transactions responses can carry preloaded initQty feeds through the same plumbing.
  integrateFeedResponse(direction, feedResponse) {
    this.#integrateFeedResponseInternal(direction, feedResponse);
  }

  #integrateFeedResponseInternal(direction, feedResponse) {
    // TODO: this case does not hit for now - still requires implementation with kill signal from back-end
    // if (feedResponse.hardReset) {
    //   this.#myStore.reset();
    // }

    if (direction === "next") {
      // @todo check the type of each remote pk field to verify "starting point values"
      this.#addFeedItemsToFeedFromResponse(feedResponse, direction);

      this.#myStore.hasMoreNext = feedResponse.hasMore;
      this.#myStore.nextOffsets = this.#computeNewOffsets(feedResponse.offsets);

      this.#updateFeedLoadingState(direction, false);
    } else {
      // @todo check the type of each remote pk field to verify "starting point values"
      this.#addFeedItemsToFeedFromResponse(feedResponse, direction);

      this.#myStore.hasMorePrevious = feedResponse.hasMore;
      this.#myStore.previousOffsets = this.#computeNewOffsets(feedResponse.offsets);

      this.#updateFeedLoadingState(direction, false);
    }

    this.#myStore.initiallyLoaded = true;

    this.#handleLastSeenOnFeedResponse(feedResponse);
  }

  #handleLastSeenOnFeedResponse(feedResponse) {
    if (feedResponse.lastSeenItemPkString) {
      this.#setLastSeenFromServer(feedResponse.lastSeenItemPkString);

      if (this.#myStore.activelyViewing && this.#myStore.lastSeenIndicatorItemPkString) {
        return;
      }

      if (!this.#myStore.lastSeenIndicatorItemPkString) {
        this.updateLastSeenIndicator(feedResponse.lastSeenItemIndicatorPkString);
      }
    }
  }

  #addFeedItemsToFeedFromResponse(feedResponse, direction) {
    feedResponse.feed.forEach(newItem => {
      if (newItem.entityType !== this.#fieldDef.entity) {
        throw new Error(`cannot add entity of type "${newItem.entityType}" to feed which expects "${this.#fieldDef.entity}"`);
      }
      this.#myStore.addItem(newItem, direction);
      // @todo need we use patch here?
    });
  }

  static #makeApiCalls() {
    if (this.#syncInProgress) {
      alert("not going to request feeds because a request is already happening");
      console.log("#paginateFeed not going to request feeds because a request is already happening");
      return;
    }
    if (Object.keys(this.#queuedFeedRequests).length === 0) {
      this.#pollingTimerId = setTimeout(() => {
        this.#makeApiCalls();
      }, this.#ttl);
      return;
    }
    this.#syncInProgress = true;
    Object.keys(this.#queuedFeedRequests).forEach(appName => {
      const deDuplicatedFeedRequests = {};
      Object.keys(this.#queuedFeedRequests[appName]).forEach(requestId => {
        deDuplicatedFeedRequests[requestId] = {};
        this.#queuedFeedRequests[appName][requestId].forEach(request => {
          const offsetKey = JSON.stringify(request.offsets);
          deDuplicatedFeedRequests[requestId][offsetKey] = request;
        });
      });
      delete this.#queuedFeedRequests[appName];
      const feedRequestsToMake = [];
      Object.keys(deDuplicatedFeedRequests).forEach(requestId => {
        Object.keys(deDuplicatedFeedRequests[requestId]).forEach(offsetKey => {
          feedRequestsToMake.push(deDuplicatedFeedRequests[requestId][offsetKey]);
        });
      });
      console.log("#paginateFeed starting the api call to fetch the feed:", JSON.stringify(feedRequestsToMake));
      const currentApp = App.get(appName);
      // POST rather than GET — feed requests can carry a large list of
      // per-feed offsets (N feeds × M offsets each, JSON-serialised into
      // `?feeds=[…]`). On mobile + long sessions that query string hits
      // the default nginx `large_client_header_buffers` limit and the
      // server returns "request header or cookie too large". A POST
      // body has no such limit.
      FeedStore.apiPost(
        `/app/${appName}/feeds`,
        {
          feeds: feedRequestsToMake,
          app_release_id: currentApp.releaseId,
        },
        response => {
          console.log("#paginateFeed data received:", JSON.stringify(response));
          // Entities that arrived via a feed query should NOT re-advance their parent
          // feed's last-seen indicator — that would be self-referential. App's shared
          // merger handles entity upsert (first, to avoid reactive lazy fetches) then
          // feed integration in a single call.
          App.get(appName).mergeResponsePayload(`/app/${appName}/feeds`, response, { shouldUpdateLastSeenIndicator: false });
        },
        e => {
          console.log("error", e, this.#transactionsSent);
          alert(`error requesting feeds: ${e.message}`);
        },
        () => {
          this.#syncInProgress = false;
          this.#pollingTimerId = setTimeout(() => {
            this.#makeApiCalls();
          }, this.#ttl);
        },
      );
    });
  }

  // ------------------------ //
  // Private Instance Methods //
  // ------------------------ //

  get #myStore() {
    return FeedStore.#stores[this.#id];
  }

  #updateFeedLastSeen(lastSeenItemPkString, isTempPkString) {
    const pkString = App.getPkString(this.#entityPk);
    const id = `${this.#appName}-${this.#entityType}-${pkString}-${this.#fieldName}`;

    if (!FeedStore.#queuedFeedRequests[this.#appName]) {
      FeedStore.#queuedFeedRequests[this.#appName] = {};
    }
    if (!FeedStore.#queuedFeedRequests[this.#appName][id]) {
      FeedStore.#queuedFeedRequests[this.#appName][id] = [];
    }

    const request = {
      entityType: this.#entityType,
      entityPk: this.#entityPk,
      fieldName: this.#fieldName,
      direction: "next",
      offsets: null,
      limit: this.#fieldDef.limit ?? 20,
      updateOnlyLastSeen: true,
      newLastSeenItemPkString: lastSeenItemPkString,
      newLastSeenItemPkStringIsTemp: isTempPkString,
    };

    const key = JSON.stringify(request);
    if (FeedStore.#queuedFeedRequestKeys[key]) {
      console.log("not queuing pagination request that is already queued");
    }
    FeedStore.#queuedFeedRequestKeys[key] = true;

    FeedStore.#queuedFeedRequests[this.#appName][id].push(request);
    if (!FeedStore.#syncInProgress) {
      clearTimeout(FeedStore.#pollingTimerId);
      FeedStore.#makeApiCalls();
    }
  }

  #paginateFeed(direction) {
    if (!["next", "previous"].includes(direction)) {
      throw new Error("#paginateFeed cannot paginate feed in unknown direction");
    }
    const pkString = App.getPkString(this.#entityPk);
    const id = `${this.#appName}-${this.#entityType}-${pkString}-${this.#fieldName}-${direction}`;
    if (!FeedStore.#queuedFeedRequests[this.#appName]) {
      FeedStore.#queuedFeedRequests[this.#appName] = {};
    }
    if (!FeedStore.#queuedFeedRequests[this.#appName][id]) {
      FeedStore.#queuedFeedRequests[this.#appName][id] = [];
    }
    // @todo we are requesting the same feed repeatedly due to reactivity issues but also FYI the bidirectional
    // scroller ask for more in addition to the initial items request determining the need for more
    // figure out a way to dedupe in the queue
    // add logs so we don't forget to figure out this reactivity performance optimization... it should only ask for more once
    const offsets = direction === "next" ? { ...this.#myStore.nextOffsets } : { ...this.#myStore.previousOffsets };
    console.log(`#paginateFeed requesting pagination of feed ${id} with offsets ${JSON.stringify(offsets)}`);

    const request = {
      entityType: this.#entityType,
      entityPk: this.#entityPk,
      fieldName: this.#fieldName,
      direction: direction,
      offsets: offsets,
      limit: this.#fieldDef.limit ?? 20,
    };

    const key = JSON.stringify(request);
    if (FeedStore.#queuedFeedRequestKeys[key]) {
      console.log("#paginateFeed not queuing pagination request that is already queued");
    }
    FeedStore.#queuedFeedRequestKeys[key] = true;

    FeedStore.#queuedFeedRequests[this.#appName][id].push({
      entityType: this.#entityType,
      entityPk: this.#entityPk,
      fieldName: this.#fieldName,
      direction: direction,
      offsets: offsets,
      limit: this.#fieldDef.limit ?? 20,
    });

    this.#updateFeedLoadingState(direction, true);
    if (!FeedStore.#syncInProgress) {
      clearTimeout(FeedStore.#pollingTimerId);
      FeedStore.#makeApiCalls();
    }
  }

  #computeInitialOffsets() {
    const app = App.get(this.#appName);
    const initialNextOffsets = {};
    const initialPreviousOffsets = {};
    const startAtEnd = this.#fieldDef.startAtEnd;
    this.#fieldDef.offsets.forEach(offset => {
      const remoteFieldDef = app.getEntityFieldDef(this.#fieldDef.entity, offset.field);
      if (!remoteFieldDef) {
        throw new Error(
          `feed "${this.#id}" offset "${offset.name}" references field "${offset.field}" `
          + `on entity "${this.#fieldDef.entity}", but that entity has no such field declared. `
          + "Declare the field in App.json or update the offset to reference a real field.",
        );
      }
      const remoteOffsetFieldType = remoteFieldDef.type;
      switch (remoteOffsetFieldType) {
        case "int":
          if (startAtEnd) {
            initialNextOffsets[offset.name] = offset.direction === "asc" ? Number.MAX_SAFE_INTEGER : 0;
            initialPreviousOffsets[offset.name] = offset.direction === "asc" ? Number.MAX_SAFE_INTEGER : 0;
          } else {
            initialNextOffsets[offset.name] = offset.direction === "asc" ? -1 : Number.MAX_SAFE_INTEGER;
            initialPreviousOffsets[offset.name] = offset.direction === "asc" ? -1 : Number.MAX_SAFE_INTEGER;
          }
          break;
        case "string":
          if (startAtEnd) {
            initialNextOffsets[offset.name] = offset.direction === "asc" ? "~" : ""; // @todo -- not 100% sure this is really min & max
            initialPreviousOffsets[offset.name] = offset.direction === "asc" ? "~" : "";
          } else {
            initialNextOffsets[offset.name] = offset.direction === "asc" ? "" : "~"; // @todo -- not 100% sure this is really min & max
            initialPreviousOffsets[offset.name] = offset.direction === "asc" ? "" : "~";
          }
          break;
        default:
          throw new Error(`offset field has unsupported type: ${remoteOffsetFieldType}`);
      }
    });
    return {
      next: initialNextOffsets,
      previous: initialPreviousOffsets,
    };
  }

  #createFeedStoreIfNotYetCreated() {
    if (!FeedStore.#stores[this.#id]) {
      const startAtEnd = this.#fieldDef.startAtEnd ?? false;
      const initialOffsets = this.#computeInitialOffsets();
      const store = defineStore(this.#id, {
        state: () => ({
          orderedKeys: [],
          items: {},
          hasMoreNext: true,
          hasMorePrevious: startAtEnd,
          isLoadingNext: false,
          isLoadingPrevious: false,
          initiallyLoaded: false,
          nextOffsets: initialOffsets.next,
          previousOffsets: initialOffsets.previous,
          lastKnownScrollPositionY: null,
          lastSeenIndicatorItemPkString: null,
          lastSeenItemIndex: 0,
          lastSeenItemPkString: null,
          activelyViewing: false,
        }),
        getters: {
          myItems: state => {
            return state.orderedKeys.map(key => state.items[key]);
          },
        },
        actions: {
          reset() {
            this.$reset();
          },
          addItem(item, direction) {
            const key = `${item.entityType}-${App.getPkString(item.entityPk)}`;
            if (this.items[key]) {
              // @TODO @NICK This is hitting consistently on my devbox - no idea why - T.P.
              // throw new Error(`cannot add item to feed because it already exists`);
              return;
            }
            this.items[key] = item;
            // @todo make this smarter, it can but perhaps should not lookup entities and compare sort values before inserting into the ordered list
            if (direction === "next") {
              if (startAtEnd) {
                this.orderedKeys.unshift(key);
              } else {
                this.orderedKeys.push(key);
              }
            } else {
              if (startAtEnd) {
                this.orderedKeys.push(key);
              } else {
                this.orderedKeys.unshift(key);
              }
            }
          },
          setLastKnownScrollPositionY(positionY) {
            this.lastKnownScrollPositionY = positionY;
          },
        },
      });
      FeedStore.#stores[this.#id] = store();
      FeedStore.#ensurePollingStarted();
    }
  }

  #updateFeedLoadingState(direction, value) {
    const startAtEnd = this.#fieldDef.startAtEnd ?? false;

    if (direction === "next" && startAtEnd) {
      this.#myStore.isLoadingPrevious = value;
    } else if (direction === "next") {
      this.#myStore.isLoadingNext = value;
    } else if (startAtEnd) {
      this.#myStore.isLoadingNext = value;
    } else {
      this.#myStore.isLoadingPrevious = value;
    }
  }

  /*
  // not sure if used or needed yet
  #storeFeedResponse(response, requestOffsets) {
    if (!_.isEqual(requestOffsets, this.#myStore.meta.offsets)) {
      throw new Error("don't store duplicate feed response");
    }
    this.#myStore.$patch({
      items: [...this.#myStore.items, ...response.feed],
      meta: response.meta,
    });
  }
  */
}
export default FeedStore;
